import React, { useState } from 'react';
import { Line, Group as KonvaGroup } from 'react-konva';
import VertexHandle from './VertexHandle';
import DrawingText from './DrawingText';
import SegmentMeasurementOverlay from './SegmentMeasurementOverlay';
import { calculateDistance, getDisplayMeasurement } from '../utils/drawingUtils';
import { DISPLAY_KEY_MEASUREMENT, DISPLAY_KEY_SEGMENT_LENGTH } from './ShapeRenderer';

// Helper to convert HEX to RGBA for professional transparency effects
const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string') return `rgba(120, 120, 120, ${alpha})`;
  const cleanHex = hex.replace('#', '');
  let r = 0, g = 0, b = 0;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
  } else {
    return hex;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CotationShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale,
  vertexMarkerRadius,
  selectedStrokeHighlightColor,
  hoverColor,
  onVertexClick,
  shouldShowText,
  displayKeys,
  textFontSize,
  activeStrokeColor,
  shapeScaleProps,
  dpi,
  imageSize,
  displayUnitSystem,
  showOverlay: showOverlayProp = true,
}) => {
  const [localHover, setLocalHover] = useState(false);

  const [x1, y1, x2, y2] = shape.points;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const L = Math.sqrt(dx * dx + dy * dy);

  if (L === 0) return null;

  const ux = dx / L;
  const uy = dy / L;
  const nx = -uy;
  const ny = ux;

  // Track hover state locally to trigger AutoCAD-style dynamic projection guides
  const handleMouseEnter = (e) => {
    setLocalHover(true);
    if (commonProps.onMouseEnter) commonProps.onMouseEnter(e);
  };

  const handleMouseLeave = (e) => {
    setLocalHover(false);
    if (commonProps.onMouseLeave) commonProps.onMouseLeave(e);
  };

  const showOverlay = (isSelected || localHover) && showOverlayProp;

  // Visual parameters scaled to screen pixels
  const offsetDist = shape.offsetDist !== undefined ? shape.offsetDist : 30 / stageScale;
  const gapDist = 4 / stageScale;
  const extPastDist = 6 / stageScale;
  const tickLen = 10 / stageScale;

  // Keep correct extension line direction depending on the offset sign
  const sign = offsetDist >= 0 ? 1 : -1;

  // Offset points
  const dim1X = x1 + offsetDist * nx;
  const dim1Y = y1 + offsetDist * ny;
  const dim2X = x2 + offsetDist * nx;
  const dim2Y = y2 + offsetDist * ny;

  const midX = (dim1X + dim2X) / 2;
  const midY = (dim1Y + dim2Y) / 2;

  const angleRad = Math.atan2(dim2Y - dim1Y, dim2X - dim1X);
  let angleDeg = angleRad * (180 / Math.PI);

  // Adjust angle for text readability
  if (angleDeg > 90) angleDeg -= 180;
  else if (angleDeg < -90) angleDeg += 180;

  // Safe measurement calculation helper
  const getMeasurementSafe = (val) => {
    return getDisplayMeasurement(
      val,
      shapeScaleProps || { ratio: 1, documentUnit: 'in' },
      dpi || 72,
      imageSize || { width: 100, height: 100 },
      displayUnitSystem || 'auto'
    );
  };

  const measurement =
    shape.displayMeasurement ||
    getMeasurementSafe(shape.distance || calculateDistance(x1, y1, x2, y2));

  // Extension line points (from shape points with small gap, extending slightly past dimension line)
  const extStart1X = x1 + gapDist * sign * nx;
  const extStart1Y = y1 + gapDist * sign * ny;
  const extEnd1X = dim1X + extPastDist * sign * nx;
  const extEnd1Y = dim1Y + extPastDist * sign * ny;

  const extStart2X = x2 + gapDist * sign * nx;
  const extStart2Y = y2 + gapDist * sign * ny;
  const extEnd2X = dim2X + extPastDist * sign * nx;
  const extEnd2Y = dim2Y + extPastDist * sign * ny;

  // Open V-wing arrowheads calculations (Style 2 arrows at the ends of dimension line)
  const arrowSize = Math.max(10 / stageScale, commonProps.strokeWidth * 3.5);
  const phi = Math.PI / 6; // 30 degree wings

  // Arrowhead 1 at dim1 pointing outwards (angleRad + Math.PI)
  const angle1 = angleRad + Math.PI;
  const p1_1X = dim1X - arrowSize * Math.cos(angle1 - phi);
  const p1_1Y = dim1Y - arrowSize * Math.sin(angle1 - phi);
  const p1_2X = dim1X - arrowSize * Math.cos(angle1 + phi);
  const p1_2Y = dim1Y - arrowSize * Math.sin(angle1 + phi);

  // Arrowhead 2 at dim2 pointing outwards (angleRad)
  const angle2 = angleRad;
  const p2_1X = dim2X - arrowSize * Math.cos(angle2 - phi);
  const p2_1Y = dim2Y - arrowSize * Math.sin(angle2 - phi);
  const p2_2X = dim2X - arrowSize * Math.cos(angle2 + phi);
  const p2_2Y = dim2Y - arrowSize * Math.sin(angle2 + phi);

  // Premium design styling tokens
  const SELECTED_GLOW = {
    shadowColor: activeStrokeColor,
    shadowBlur: 14,
    shadowOpacity: 0.6,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
  };

  return (
    <KonvaGroup
      {...commonProps}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Reusable Segment Projection Guide Overlay */}
      <SegmentMeasurementOverlay
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        activeStrokeColor={activeStrokeColor}
        stageScale={stageScale}
        textFontSize={textFontSize}
        shapeScaleProps={shapeScaleProps}
        dpi={dpi}
        imageSize={imageSize}
        displayUnitSystem={displayUnitSystem}
        showOverlay={showOverlay}
      />

      {/* 5. Main Dimension Line (thinned for AutoCAD elegance) */}
      <Line
        name="visual_shape"
        points={[dim1X, dim1Y, dim2X, dim2Y]}
        stroke={commonProps.stroke}
        strokeWidth={commonProps.strokeWidth * 0.75}
        lineCap="round"
        perfectDrawEnabled={false}
        listening={true}
        {...(isSelected ? SELECTED_GLOW : {})}
      />

      {/* 6. Extension Line 1 (solid, thinned, semi-transparent) */}
      <Line
        points={[extStart1X, extStart1Y, extEnd1X, extEnd1Y]}
        stroke={hexToRgba(commonProps.stroke, 0.4)}
        strokeWidth={commonProps.strokeWidth * 0.5}
        perfectDrawEnabled={false}
        listening={false}
      />

      {/* 7. Extension Line 2 (solid, thinned, semi-transparent) */}
      <Line
        points={[extStart2X, extStart2Y, extEnd2X, extEnd2Y]}
        stroke={hexToRgba(commonProps.stroke, 0.4)}
        strokeWidth={commonProps.strokeWidth * 0.5}
        perfectDrawEnabled={false}
        listening={false}
      />

      {/* 8. Open V-Wings Arrowhead 1 (Left/Start) */}
      <Line
        points={[p1_1X, p1_1Y, dim1X, dim1Y, p1_2X, p1_2Y]}
        stroke={commonProps.stroke}
        strokeWidth={commonProps.strokeWidth * 0.75}
        lineCap="round"
        lineJoin="round"
        perfectDrawEnabled={false}
        listening={false}
      />

      {/* 9. Open V-Wings Arrowhead 2 (Right/End) */}
      <Line
        points={[p2_1X, p2_1Y, dim2X, dim2Y, p2_2X, p2_2Y]}
        stroke={commonProps.stroke}
        strokeWidth={commonProps.strokeWidth * 0.75}
        lineCap="round"
        lineJoin="round"
        perfectDrawEnabled={false}
        listening={false}
      />

      {/* 10. Main Measurement Text Badge */}
      {shouldShowText && (
        <DrawingText
          key={`cotation-text-${shape.id}`}
          x={midX}
          y={midY}
          text={`${measurement.value} ${measurement.unit}`}
          fontSize={textFontSize}
          fill={activeStrokeColor}
          rotation={angleDeg}
          offsetX={0}
          offsetY={textFontSize * 0.8}
          stageScale={stageScale}
          useBadge={true}
          badgeStroke={activeStrokeColor}
        />
      )}

      {/* 11. Vertex handles for selected Cotation */}
      {isSelected &&
        shape.points &&
        shape.points.map((_, index) => {
          if (index % 2 === 0) {
            const vertexX = shape.points[index];
            const vertexY = shape.points[index + 1];
            return (
              <VertexHandle
                key={`vtx-cotation-${shape.id}-${index / 2}`}
                x={vertexX}
                y={vertexY}
                radius={vertexMarkerRadius}
                color={selectedStrokeHighlightColor}
                hoverColor={hoverColor}
                onMouseDown={(e) => onVertexClick(e, shape.id, index / 2)}
                onTouchStart={(e) => onVertexClick(e, shape.id, index / 2)}
                stageScale={stageScale}
              />
            );
          }
          return null;
        })}
      {isSelected && (
        <VertexHandle
          key={`vtx-cotation-offset-${shape.id}`}
          x={midX}
          y={midY}
          radius={vertexMarkerRadius * 1.25}
          color="#10B981"
          hoverColor="#34D399"
          onMouseDown={(e) => onVertexClick(e, shape.id, 2)}
          onTouchStart={(e) => onVertexClick(e, shape.id, 2)}
          stageScale={stageScale}
        />
      )}
    </KonvaGroup>
  );
};

export default CotationShape;
